import React, { useEffect } from "react";
import { useGetAiMetricsQuery, type AiMetricsResponse } from "@/store/admin_api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FaMicrophone,
  FaBrain,
  FaVolumeUp,
  FaBolt,
  FaExclamationTriangle,
  FaTools,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import type { IconType } from "react-icons";

// ── Local type aliases ────────────────────────────────────────────────────────

type LatencyStats = AiMetricsResponse["e2eTurnLatency"];

// ── Helpers ──────────────────────────────────────────────────────────────────

function latencyColour(meanMs: number, thresholdWarn = 500, thresholdBad = 1000) {
  if (meanMs === 0) return "text-muted-foreground";
  if (meanMs < thresholdWarn) return "text-green-500";
  if (meanMs < thresholdBad) return "text-yellow-500";
  return "text-red-500";
}

function latencyBadgeVariant(meanMs: number, warnMs: number, badMs: number): string {
  if (meanMs === 0) return "outline";
  if (meanMs < warnMs) return "outline";
  if (meanMs < badMs) return "outline";
  return "outline";
}

function latencyLabel(meanMs: number, warnMs: number, badMs: number) {
  if (meanMs === 0) return null;
  if (meanMs < warnMs) return { text: "Good", cls: "bg-success/10 text-success border-success/30" };
  if (meanMs < badMs) return { text: "Slow", cls: "bg-warning/10 text-warning border-warning/30" };
  return { text: "High", cls: "bg-destructive/10 text-destructive border-destructive/30" };
}

function ms(value: number) {
  return value === 0 ? "—" : `${value} ms`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
  );
}

function LatencyCard({
  title,
  subtitle,
  Icon,
  stats,
  warnMs = 500,
  badMs = 1000,
}: {
  title: string;
  subtitle: string;
  Icon: IconType;
  stats: LatencyStats;
  warnMs?: number;
  badMs?: number;
}) {
  const colour = latencyColour(stats.meanMs, warnMs, badMs);
  const hasPercentiles = stats.p50Ms > 0 || stats.p95Ms > 0 || stats.p99Ms > 0;
  const label = latencyLabel(stats.meanMs, warnMs, badMs);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-muted p-1.5">
              <Icon className="text-muted-foreground w-3.5 h-3.5" />
            </div>
            <CardTitle className="text-sm font-semibold leading-tight">{title}</CardTitle>
          </div>
          {label && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ${label.cls}`}>
              {label.text}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{subtitle}</p>
      </CardHeader>
      <CardContent className="flex-1">
        {stats.count === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
            No data yet — make a Native AI call to populate
          </div>
        ) : (
          <>
            <div className="flex items-end gap-4 mb-3">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Avg</p>
                <p className={`text-2xl font-bold tabular-nums ${colour}`}>{ms(stats.meanMs)}</p>
              </div>
              <div className="pb-0.5">
                <p className="text-xs text-muted-foreground mb-0.5">Max</p>
                <p className="text-base font-semibold tabular-nums text-muted-foreground">{ms(stats.maxMs)}</p>
              </div>
              <div className="ml-auto pb-0.5">
                <Badge variant="outline" className="text-xs font-normal">
                  {stats.count} turns
                </Badge>
              </div>
            </div>
            {hasPercentiles && (
              <div className="grid grid-cols-3 gap-1 border-t pt-2 text-center">
                <div>
                  <p className="text-[10px] text-muted-foreground">P50</p>
                  <p className="text-xs font-medium tabular-nums">{ms(stats.p50Ms)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">P95</p>
                  <p className="text-xs font-medium tabular-nums">{ms(stats.p95Ms)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">P99</p>
                  <p className="text-xs font-medium tabular-nums">{ms(stats.p99Ms)}</p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        <p className="text-xs text-muted-foreground leading-relaxed">{subtitle}</p>
      </CardHeader>
      <CardContent className="flex-1">{children}</CardContent>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48 mt-1" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-3/4" />
      </CardContent>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AiPerformanceDashboard() {
  const { data, isLoading, isError, refetch } = useGetAiMetricsQuery(undefined, {
    pollingInterval: 30_000,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const metrics = data as AiMetricsResponse | undefined;
  const totalRequests = metrics?.llmRequests.total ?? 0;
  const errorRate = metrics?.llmRequests.errorRatePct ?? 0;

  return (
    <div className="space-y-8">

      {/* ── Page header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Performance</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Real-time pipeline metrics for Native AI calls. Latencies are measured from
            the moment the caller stops speaking to the moment audio is sent back.
            Resets on server restart. Refreshes every 30 s.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="text-xs text-muted-foreground hover:text-foreground underline shrink-0 mt-1"
        >
          Refresh now
        </button>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <FaExclamationTriangle className="shrink-0" />
          Failed to load metrics. Check that you are logged in as a platform admin.
        </div>
      )}

      {/* ── Pipeline latency ── */}
      <section>
        <SectionHeading
          title="Pipeline Latency"
          description="How long each stage of the AI pipeline takes per conversation turn. Lower is better — anything above the threshold turns yellow or red."
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          ) : (
            <>
              <LatencyCard
                title="End-to-End Turn"
                subtitle="Total time from when the caller stops speaking to when they hear the first word of the AI's reply. This is what the caller actually experiences as the 'response delay'."
                Icon={FaBolt}
                stats={metrics?.e2eTurnLatency ?? emptyLatency()}
                warnMs={800}
                badMs={1500}
              />
              <LatencyCard
                title="Speech Recognition"
                subtitle="How long Deepgram takes to transcribe the caller's speech into text after they stop talking. Delays here make the AI feel unresponsive even before it starts thinking."
                Icon={FaMicrophone}
                stats={metrics?.sttLatency ?? emptyLatency()}
                warnMs={300}
                badMs={600}
              />
              <LatencyCard
                title="AI Thinking (LLM)"
                subtitle="Time from receiving the caller's transcript to the AI producing its first complete sentence. This is where most of the delay usually lives — depends on model speed and network."
                Icon={FaBrain}
                stats={metrics?.llmLatency ?? emptyLatency()}
                warnMs={400}
                badMs={900}
              />
              <LatencyCard
                title="Voice Synthesis (TTS)"
                subtitle="How long ElevenLabs takes to convert the AI's text response into the first audio chunk. A pre-warmed WebSocket connection keeps this under 300 ms in normal conditions."
                Icon={FaVolumeUp}
                stats={metrics?.ttsLatency ?? emptyLatency()}
                warnMs={200}
                badMs={500}
              />
            </>
          )}
        </div>
      </section>

      {/* ── Provider health ── */}
      <section>
        <SectionHeading
          title="Provider Health"
          description="Request success and failure counts for the LLM (AI thinking) and TTS (voice synthesis) providers since the last server start."
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          ) : (
            <>
              {/* LLM requests */}
              <StatCard
                title="LLM Requests"
                subtitle="Each conversation turn triggers one LLM request. A high total means the system is being used actively."
              >
                <p className="text-3xl font-bold tabular-nums">{totalRequests}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <FaCheckCircle className="w-3 h-3" />
                    {metrics?.llmRequests.success ?? 0} ok
                  </span>
                  <span className="flex items-center gap-1 text-red-500">
                    <FaTimesCircle className="w-3 h-3" />
                    {metrics?.llmRequests.error ?? 0} failed
                  </span>
                </div>
              </StatCard>

              {/* LLM error rate */}
              <StatCard
                title="LLM Error Rate"
                subtitle="Percentage of LLM requests that failed. Above 5% usually means a rate-limit or API key issue."
              >
                <p
                  className={`text-3xl font-bold tabular-nums ${
                    errorRate === 0 ? "text-green-500" : errorRate < 5 ? "text-yellow-500" : "text-red-500"
                  }`}
                >
                  {errorRate}%
                </p>
                {errorRate === 0 ? (
                  <p className="text-xs text-green-600 mt-2">All requests succeeded</p>
                ) : (
                  <div className="mt-2 text-xs text-muted-foreground space-y-1">
                    <p>Bad response: <span className="font-medium">{metrics?.llmErrors.llmError ?? 0}</span></p>
                    <p>Network / timeout: <span className="font-medium">{metrics?.llmErrors.exception ?? 0}</span></p>
                  </div>
                )}
              </StatCard>

              {/* TTS requests */}
              <StatCard
                title="TTS Requests"
                subtitle="How many voice synthesis requests were made to ElevenLabs. Each sentence the agent speaks is one request."
              >
                <p className="text-3xl font-bold tabular-nums">{metrics?.ttsRequests.total ?? 0}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <FaCheckCircle className="w-3 h-3" />
                    {metrics?.ttsRequests.success ?? 0} ok
                  </span>
                  <span className="flex items-center gap-1 text-red-500">
                    <FaTimesCircle className="w-3 h-3" />
                    {metrics?.ttsRequests.error ?? 0} failed
                  </span>
                </div>
              </StatCard>

              {/* TTS error rate */}
              <StatCard
                title="TTS Error Rate"
                subtitle="Percentage of voice synthesis requests that failed. Any failures here mean the caller heard silence instead of speech."
              >
                {(() => {
                  const ttsRate = metrics?.ttsRequests.errorRatePct ?? 0;
                  return (
                    <>
                      <p className={`text-3xl font-bold tabular-nums ${ttsRate === 0 ? "text-green-500" : ttsRate < 5 ? "text-yellow-500" : "text-red-500"}`}>
                        {ttsRate}%
                      </p>
                      <p className={`text-xs mt-2 ${ttsRate === 0 ? "text-green-600" : "text-red-500"}`}>
                        {ttsRate === 0 ? "All synthesis succeeded" : "Check ElevenLabs API key / quota"}
                      </p>
                    </>
                  );
                })()}
              </StatCard>
            </>
          )}
        </div>
      </section>

      {/* ── Call behaviour ── */}
      <section>
        <SectionHeading
          title="Call Behaviour"
          description="How callers interact with the AI during conversations."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {isLoading ? (
            Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={i} />)
          ) : (
            <>
              <StatCard
                title="Barge-ins"
                subtitle="How many times the caller started speaking while the AI was still talking. A healthy number — means the caller is engaged. Very high numbers may indicate the AI is too slow or long-winded."
              >
                <p className="text-3xl font-bold tabular-nums">{metrics?.bargeIns ?? 0}</p>
                {totalRequests > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {`${Math.round(((metrics?.bargeIns ?? 0) / totalRequests) * 100)}% of turns interrupted`}
                  </p>
                )}
              </StatCard>

              <StatCard
                title="Tool Loop Cap Hits"
                subtitle="How many times the AI hit the maximum allowed tool calls in a single turn. Should always be 0 — a non-zero value means the AI got stuck in a loop trying to use tools."
              >
                <p className={`text-3xl font-bold tabular-nums ${(metrics?.toolIterationCapHits ?? 0) > 0 ? "text-red-500" : "text-green-500"}`}>
                  {metrics?.toolIterationCapHits ?? 0}
                </p>
                <p className={`text-xs mt-2 ${(metrics?.toolIterationCapHits ?? 0) > 0 ? "text-red-500" : "text-green-600"}`}>
                  {(metrics?.toolIterationCapHits ?? 0) === 0 ? "No loops detected" : "Investigate tool definitions"}
                </p>
              </StatCard>
            </>
          )}
        </div>
      </section>

      {/* ── Tool invocations ── */}
      <section>
        <SectionHeading
          title="Tool Invocations"
          description="How many times the AI called each tool during conversations. end_call is the most common — it fires once per completed call."
        />
        {isLoading ? (
          <SkeletonCard />
        ) : (
          <Card>
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
              <div className="rounded-md bg-muted p-1.5">
                <FaTools className="text-muted-foreground w-3.5 h-3.5" />
              </div>
              <CardTitle className="text-sm font-semibold">By Tool</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(metrics?.toolInvocations ?? {}).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No tool calls recorded yet. Tools are called during Native AI conversations
                  (e.g. <code className="text-xs bg-muted px-1 py-0.5 rounded">end_call</code>).
                </p>
              ) : (
                <div className="divide-y">
                  {Object.entries(metrics?.toolInvocations ?? {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([tool, count]) => (
                      <div key={tool} className="flex items-center justify-between py-2.5">
                        <div>
                          <code className="text-sm font-mono">{tool}</code>
                          {tool === "end_call" && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              AI ended the call — fires once per completed conversation
                            </p>
                          )}
                        </div>
                        <Badge variant="secondary" className="tabular-nums">{count}×</Badge>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </section>

    </div>
  );
}

function emptyLatency(): LatencyStats {
  return { count: 0, meanMs: 0, maxMs: 0, p50Ms: 0, p95Ms: 0, p99Ms: 0 };
}
